import { handleError } from "@/utils/error";
import {
  useProposal,
  useGenerateProposalReport,
  useDownloadProposalReport,
  webDownload,
} from "creso-core/hooks";
import { sleep } from "radash";
import { useCallback } from "react";

const MAX_ATTEMPTS = 10;

export const useDownloadProposal = (proposalId: number) => {
  const { refetch } = useProposal({ proposalId }, { enabled: false });

  const generateProposalReportMutation = useGenerateProposalReport();

  const downloadProposalReportMutation = useDownloadProposalReport();

  const isReadyForDownload = useCallback(
    async (attempt = 1) => {
      if (attempt > MAX_ATTEMPTS) {
        throw new Error("Operation timed out");
      }
      const { data } = await refetch();
      if (data?.pdf_status === "READY" && data?.pdf_file_available) return;
      if (data?.pdf_status === "FAILED") {
        throw new Error("Failed to generate proposal report");
      }
      await sleep(3000);
      await isReadyForDownload(attempt + 1);
    },
    [refetch]
  );

  return useCallback(
    async (url: string) => {
      try {
        const { data: proposal } = await refetch();
        if (!proposal) {
          throw new Error("Proposal not found");
        }
        if (!["GENERATING"].includes(proposal.pdf_status)) {
          await generateProposalReportMutation.mutateAsync({
            id: proposalId,
            url,
          });
        }
        await isReadyForDownload();
        const blob =
          await downloadProposalReportMutation.mutateAsync(proposalId);
        await webDownload(blob, `${proposal.name}.pdf`);
        return true;
      } catch (e) {
        handleError(e, "Failed to download proposal report");
        return false;
      }
    },
    [
      refetch,
      isReadyForDownload,
      generateProposalReportMutation,
      downloadProposalReportMutation,
      proposalId,
    ]
  );
};
